import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Clock, BadgeCheck, Star } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1635424824800-692767998d07?crop=entropy&cs=srgb&fm=jpg&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const PILLS = [
  { icon: ShieldCheck, text: "Licensed & Insured in NJ, DE, PA" },
  { icon: BadgeCheck, text: "25-Year Warranty" },
  { icon: Clock, text: "Same-Day Emergency Response" },
];

export const Hero = () => {
  const sectionRef = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 18 });
  const sry = useSpring(ry, { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      className="hero-grid-lines relative overflow-hidden pt-28 lg:pt-36"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -top-40 left-1/4 h-[480px] w-[480px] rounded-full bg-orange-500/10 blur-[140px]" />
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {[
          { left: "5%", d: "5.2s", delay: "0s" },
          { left: "14%", d: "6.4s", delay: "1.2s" },
          { left: "27%", d: "5.8s", delay: "2.4s" },
          { left: "43%", d: "6.8s", delay: "0.6s" },
          { left: "58%", d: "5.5s", delay: "1.8s" },
          { left: "71%", d: "6.1s", delay: "3s" },
          { left: "84%", d: "5.9s", delay: "0.9s" },
          { left: "94%", d: "6.6s", delay: "2.1s" },
        ].map((r, i) => (
          <span
            key={i}
            className="rain-line"
            style={{ left: r.left, animationDuration: r.d, animationDelay: r.delay }}
          />
        ))}
      </div>
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-20 sm:px-8 lg:grid-cols-12 lg:pb-28">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="mb-6 font-mono text-xs font-bold uppercase tracking-[0.3em] text-orange-500"
            data-testid="hero-overline"
          >
            Tri-State's Shield — NJ · DE · PA
          </motion.p>
          <h1 className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-8xl">
            <MaskedLine delay={0.25}>Defend Your</MaskedLine>
            <MaskedLine delay={0.4}>
              Home <span className="text-outline">Against</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="text-orange-500">Water Damage</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-subheadline"
          >
            Custom seamless gutters, high-flow downspouts &amp; sub-grade
            waterproofing — built with you, styled to your home, and guaranteed
            across New Jersey, Delaware &amp; Pennsylvania.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-5 font-serif text-xl italic text-slate-300 sm:text-2xl"
            data-testid="hero-tagline"
          >
            "A home essential, with a taste of design."
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#quote"
              data-testid="hero-primary-quote-btn"
              className="animate-pulse-ring rounded-full bg-orange-500 px-7 py-4 text-sm font-bold uppercase tracking-wide text-ink transition-all hover:-translate-y-0.5 hover:bg-orange-600 hover:text-white"
            >
              Request Free Inspection &amp; Quote
            </a>
            <a
              href="#coverage"
              data-testid="hero-secondary-coverage-btn"
              className="rounded-full border border-white/20 px-7 py-4 text-sm font-bold uppercase tracking-wide text-slate-200 transition-colors hover:border-orange-500/60 hover:text-orange-500"
            >
              View Coverage Area
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            {PILLS.map(({ icon: Icon, text }) => (
              <span
                key={text}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-slate-300"
              >
                <Icon className="h-4 w-4 text-orange-500" /> {text}
              </span>
            ))}
          </motion.div>
        </div>
        <div className="lg:col-span-5" style={{ perspective: 1000 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            onMouseMove={onMove}
            onMouseLeave={() => {
              rx.set(0);
              ry.set(0);
            }}
            style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
            className="relative"
            data-testid="hero-image-card"
          >
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-orange-500/30 via-transparent to-transparent blur-xl" />
            <div className="relative overflow-hidden rounded-2xl border border-white/15">
              <motion.img
                src={HERO_IMG}
                alt="Crew installing seamless gutters on a roofline"
                className="h-[420px] w-full object-cover sm:h-[520px]"
                style={{ y: imgY, scale: 1.12 }}
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-xl border border-white/15 bg-ink/70 px-5 py-4 backdrop-blur-md">
                <div>
                  <p className="font-display text-xl font-bold uppercase text-white">
                    2,400+ Systems
                  </p>
                  <p className="text-xs text-slate-400">Installed &amp; Sealed</p>
                </div>
                <div className="flex items-center gap-1 text-orange-500">
                  <Star className="h-5 w-5 fill-orange-500" />
                  <span className="font-display text-xl font-bold text-white">4.9</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
